import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Box from "apollo-react/components/Box";
import Typography from "apollo-react/components/Typography";
import Button from "apollo-react/components/Button";
import Download from "apollo-react-icons/Download";
import axiosInstance from "../../../axiosInstance";
import CommentToggle from "../CommentToggle/CommentToggle";
import CommentsList from "../FormApproveComponents/CommentsList/CommentsList";

/**
 * Additional Documents Tab Component
 * Shows certificate (if uploaded) and allows all roles to add comments/attachments
 * Supports both:
 * - New request mode (no requestId): stores comments in context for submission
 * - Existing request mode (with requestId): saves comments to API
 */
const AdditionalDocuments = ({ 
  requestId, 
  certificateFileId, 
  role = '',
  persona = 'admin',
  requestProgressStatus = '',
  comments = [],
  setComments = () => {},
  tempComments = [],
  onAddTempComment = null,
  onEditTempComment = null,
  onDeleteTempComment = null,
  isNewRequest = false,
}) => {
  const { t } = useTranslation();
  const [certificateFile, setCertificateFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  // For new requests, comments are already "loaded" from context
  const [commentsLoaded, setCommentsLoaded] = useState(isNewRequest);

  // Fetch certificate
  useEffect(() => {
    const fetchCertificate = async () => {
      if (!certificateFileId || !requestId) {
        return;
      }

      try {
        const endpoint = persona === 'customer' 
          ? `/api/requests/enduser/${requestId}/certificate`
          : `/api/requests/${requestId}/certificate`;
        
        const response = await axiosInstance.get(endpoint);
        if (response.data.success) {
          setCertificateFile(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching certificate:', error);
      }
    };

    fetchCertificate();
  }, [certificateFileId, requestId, persona]);

  // Fetch comments (only for existing requests)
  const fetchComments = useCallback(async () => {
    if (isNewRequest) {
      // For new requests, don't fetch - use tempComments from context
      setCommentsLoaded(true);
      return;
    }

    if (!requestId) {
      setCommentsLoaded(true);
      return;
    }
    
    try {
      const endpoint = persona === 'customer'
        ? `/api/additional-documents-comments/enduser/${requestId}`
        : `/api/additional-documents-comments/${requestId}`;
      
      const response = await axiosInstance.get(endpoint);
      setComments(response.data || []);
      setCommentsLoaded(true);
    } catch (error) {
      console.error('Error fetching additional documents comments:', error);
      setCommentsLoaded(true);
    }
  }, [requestId, persona, setComments, isNewRequest]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Determine which comments to display - use tempComments from context for new requests
  const displayComments = isNewRequest ? (tempComments || []) : (comments || []);

  // Handle certificate download
  const handleDownloadCertificate = async () => {
    if (!certificateFile) return;
    
    try {
      setDownloading(true);
      const response = await axiosInstance.get(
        `/api/files/download/${certificateFile.id}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', certificateFile.original_name || 'certificate');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
    } finally {
      setDownloading(false);
    }
  };

  // Handle adding a new comment
  const handleAddComment = useCallback(async (comment, subFormId, fileId = null, fileData = null) => {
    // For new requests, use temp comment handler from context
    if (isNewRequest && onAddTempComment) {
      const newComment = {
        comment,
        additional_file_id: fileId || null,
        additional_file: fileData ? {
          id: fileData.id,
          original_name: fileData.original_name,
          file_path: null,
          file_size: fileData.file_size,
          mime_type: fileData.mime_type,
          createdAt: fileData.created_at,
          updatedAt: fileData.created_at,
          deletedAt: null,
          user_id: null,
          end_user_id: null,
          ...fileData
        } : null,
        end_user: {
          username: t('additionalDocuments.you') || 'You',
          email: '',
          end_users_category: { name: 'Applicant' }
        },
      };
      onAddTempComment(newComment);
      return;
    }

    // For existing requests, use API
    try {
      const requestData = {
        requestId,
        comment,
      };
      
      if (fileId) {
        requestData.fileId = fileId;
      }

      const endpoint = persona === 'customer'
        ? `/api/additional-documents-comments/enduser`
        : `/api/additional-documents-comments`;
      
      const response = await axiosInstance.post(endpoint, requestData);
      
      const enrichedComment = { ...response.data, isOwner: true };
      if (fileData && fileId) {
        enrichedComment.additional_file = {
          id: fileData.id,
          original_name: fileData.original_name,
          file_path: null,
          file_size: fileData.file_size,
          mime_type: fileData.mime_type,
          createdAt: fileData.created_at,
          updatedAt: fileData.created_at,
          deletedAt: null,
          user_id: null,
          end_user_id: null,
          ...fileData
        };
      }
      
      setComments(prev => [enrichedComment, ...prev]);
    } catch (error) {
      console.error('Error adding additional documents comment:', error);
      throw error;
    }
  }, [requestId, persona, setComments, isNewRequest, onAddTempComment]);

  // handle edit comment
  const handleEditComment = useCallback(async (commentId, newText, attachmentInfo = {}) => {
    // For new requests, use temp comment handler
    if (isNewRequest && onEditTempComment) {
      const updates = { comment: newText };
      if (attachmentInfo.remove_attachment) {
        updates.additional_file = null;
        updates.additional_file_id = null;
      } else if (attachmentInfo.new_file_id && attachmentInfo.new_file_data) {
        updates.additional_file_id = attachmentInfo.new_file_id;
        updates.additional_file = {
          id: attachmentInfo.new_file_data.id,
          original_name: attachmentInfo.new_file_data.original_name,
          file_size: attachmentInfo.new_file_data.file_size,
          mime_type: attachmentInfo.new_file_data.mime_type,
          ...attachmentInfo.new_file_data
        };
      }
      onEditTempComment(commentId, updates);
      return;
    }

    // For existing requests, use API
    try {
      const requestData = { comment: newText };
      
      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }

      const endpoint = persona === 'customer'
        ? `/api/additional-documents-comments/enduser/${commentId}`
        : `/api/additional-documents-comments/${commentId}`;
      
      await axiosInstance.put(endpoint, requestData);
      fetchComments();
    } catch (error) {
      console.error('error editing comment:', error);
      throw error;
    }
  }, [persona, fetchComments, isNewRequest, onEditTempComment]);

  // handle delete comment
  const handleDeleteComment = useCallback(async (commentId) => {
    // For new requests, use temp comment handler
    if (isNewRequest && onDeleteTempComment) {
      onDeleteTempComment(commentId);
      return;
    }

    // For existing requests, use API
    try {
      const endpoint = persona === 'customer'
        ? `/api/additional-documents-comments/enduser/${commentId}`
        : `/api/additional-documents-comments/${commentId}`;
      
      await axiosInstance.delete(endpoint);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('error deleting comment:', error);
      throw error;
    }
  }, [persona, setComments, isNewRequest, onDeleteTempComment]);

  // Check if request is ended (ra-approved or any rejection)
  const isRequestEnded = requestProgressStatus && (
    requestProgressStatus.includes('ra-approved') || 
    requestProgressStatus.includes('rejected')
  );

  // Check if comment form should be initially open
  // For new requests: open if no temp comments yet
  // For existing requests: open if no comments exist after loading
  const shouldOpenCommentForm = isNewRequest 
    ? displayComments.length === 0 
    : (commentsLoaded && displayComments.length === 0 && !isRequestEnded);

  // Allow adding comments for new requests or existing non-ended requests
  const canAddComment = isNewRequest || (requestId && !isRequestEnded && commentsLoaded);

  return (
    <div>
      {/* Certificate Section - Pinned at top, styled like comment attachments */}
      {certificateFile && (
        <Box sx={{ 
          marginBottom: 4,
          padding: "12px 16px",
          backgroundColor: "#e3f2fd", // Light blue background
          borderRadius: "4px",
          border: "1px solid rgba(7, 104, 253, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, overflow: "hidden" }}>
            <Typography
              variant="bodyDefault"
              style={{ 
                fontWeight: 600,
                flexShrink: 0
              }}
            >
              Drug Certificate:
            </Typography>
            <Typography
              variant="bodyDefault"
              style={{ 
                fontWeight: 400,
                color: "#333",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {certificateFile.original_name}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            endIcon={<Download size={11} />}
            onClick={handleDownloadCertificate}
            disabled={downloading}
            sx={{
              minWidth: "110px",
              flexShrink: 0,
              '& .MuiButton-endIcon': {
                marginLeft: "4px",
              }
            }}
          >
            {downloading ? "Downloading..." : "Download"}
          </Button>
        </Box>
      )}

      {/* Comments Section */}
      <div 
        className="form-comment-section" 
        style={{ 
          isolation: 'isolate', 
          position: 'relative', 
          zIndex: 1,
          marginTop: certificateFile ? '35px' : '0'
        }}
      >
        {/* Display all comments - simple list like ECTD */}
        <CommentsList
          prevComments={displayComments}
          pagination={{}}
          filters={{}}
          updateFilters={() => {}}
          persona={persona}
          showControls={false}
          showFilters={false}
          showSorting={false}
          showPageSize={false}
          showResultsInfo={false}
          showPagination={false}
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
        />

        {/* Show Add Comment button if allowed */}
        {canAddComment && (
          <CommentToggle
            onAddComment={handleAddComment}
            persona={persona}
            buttonText={t('additionalDocuments.addComment')}
            buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
            initiallyOpen={shouldOpenCommentForm}
            hasComments={displayComments && displayComments.length > 0}
          />
        )}
      </div>
    </div>
  );
};

export default AdditionalDocuments;

